/**
 * AttendeesDropdown - Suggestion list for AttendeesInput.
 *
 * Handles click-outside detection internally. Parent passes containerRef
 * so clicks inside the input container don't close the dropdown.
 */

import { useEffect, useRef } from 'react';

import { Avatar } from '@/lib/base/avatar/avatar';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import type { ContactResult, UserResult } from '@/lib/search';
import { shouldIgnoreOutsideInteraction } from '@/utils/third-party-editor-ui';

interface AttendeesDropdownProps {
  suggestions: (UserResult | ContactResult)[];
  highlightedIndex: number;
  onHighlightChange: (index: number) => void;
  onSelect: (entity: UserResult | ContactResult) => void;
  isLoading: boolean;
  hasFinishedSearching: boolean;
  instanceId: string;
  listboxId: string;
  containerRef: React.RefObject<HTMLDivElement | null>;
  onClose: () => void;
}

export function AttendeesDropdown({
  suggestions,
  highlightedIndex,
  onHighlightChange,
  onSelect,
  isLoading,
  hasFinishedSearching,
  instanceId,
  listboxId,
  containerRef,
  onClose,
}: AttendeesDropdownProps) {
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (shouldIgnoreOutsideInteraction(event.target)) {
        return;
      }
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [containerRef, onClose]);

  return (
    <div
      ref={dropdownRef}
      id={listboxId}
      role='listbox'
      aria-label='Attendees suggestions'
      className='absolute left-0 right-0 top-full z-50 mt-1 max-h-48 overflow-auto rounded-lg border border-border-primary bg-bg-primary shadow-lg'
    >
      {isLoading && (
        <div className='flex items-center gap-2 px-3 py-2'>
          <div className='size-6 flex items-center justify-center'>
            <LoadingIndicator inline size='xs' />
          </div>
          <span className='text-sm text-fg-quaternary'>Searching…</span>
        </div>
      )}
      {!isLoading &&
        suggestions.map((entity, index) => (
          <div
            key={`${entity.type}-${entity.id}`}
            id={`${instanceId}-option-${index}`}
            role='option'
            aria-selected={highlightedIndex === index}
            className={`flex cursor-pointer items-center gap-2 px-3 py-2 ${
              highlightedIndex === index
                ? 'bg-bg-secondary'
                : 'hover:bg-bg-secondary'
            }`}
            onClick={() => onSelect(entity)}
            onMouseEnter={() => onHighlightChange(index)}
          >
            {entity.avatar_url ? (
              <Avatar size='xs' src={entity.avatar_url} alt={entity.name} />
            ) : (
              <div className='flex size-6 items-center justify-center rounded-full bg-bg-tertiary text-xs font-medium text-fg-secondary'>
                {entity.name.charAt(0).toUpperCase()}
              </div>
            )}
            <div className='flex min-w-0 flex-1 flex-col'>
              <span className='truncate text-sm font-medium text-fg-primary'>
                {entity.name}
              </span>
              <span className='truncate text-xs text-fg-tertiary'>
                {entity.email}
              </span>
            </div>
            <span className='shrink-0 rounded-md bg-bg-tertiary px-1.5 py-0.5 text-xs text-fg-secondary'>
              {entity.type === 'ahoy_user' ? 'User' : 'Contact'}
            </span>
          </div>
        ))}
      {!isLoading && suggestions.length === 0 && hasFinishedSearching && (
        <div className='px-3 py-3 text-center text-sm text-fg-tertiary'>
          No results. Press Enter to add as email.
        </div>
      )}
    </div>
  );
}
